import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Sparkles, RefreshCw, AlertTriangle, TrendingDown, Clock, Truck, PackageOpen, Info } from "lucide-react";

const iconFor = (kind) => {
  const map = {
    truck_idle: Truck,
    inventory_low: PackageOpen,
    inventory_reorder: TrendingDown,
    job_overdue: Clock,
    fleet_underused: Truck,
    alert: AlertTriangle,
  };
  return map[kind] || Info;
};

const toneFor = (severity) => {
  if (severity === "critical") return "border-l-red-500 bg-red-500/5 text-red-500";
  if (severity === "warning") return "border-l-amber-500 bg-amber-500/5 text-amber-500";
  if (severity === "success") return "border-l-emerald-500 bg-emerald-500/5 text-emerald-500";
  return "border-l-sky-500 bg-sky-500/5 text-sky-500";
};

export default function AIDispatchPanel({ plantId }) {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/ai/insights${plantId ? `?plant_id=${plantId}` : ""}`);
      setInsights(r.data.insights || []);
      setRefreshedAt(new Date());
    } catch {
      setInsights([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60 * 1000); // refresh every 60s
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plantId]);

  return (
    <Card className="card-v2 p-5" data-testid="ai-dispatch-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg bg-primary-orange/15 border border-primary-orange/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-primary-orange" />
          </div>
          <div>
            <div className="label-tag">AI Dispatch · Insights</div>
            <div className="text-[10px] mono text-muted-industrial mt-0.5">
              {refreshedAt ? `Updated ${refreshedAt.toLocaleTimeString()}` : "Analysing plant data…"}
            </div>
          </div>
        </div>
        <button
          data-testid="ai-refresh-btn"
          onClick={load}
          disabled={loading}
          className="mono text-[10px] uppercase tracking-widest text-muted-industrial hover:text-primary-orange inline-flex items-center gap-1 disabled:opacity-50"
          title="Refresh insights"
        >
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {loading && insights.length === 0 && (
        <div className="text-xs text-muted-industrial mono">Reading GPS + inventory + jobs…</div>
      )}

      {!loading && insights.length === 0 && (
        <div className="border border-dashed border-industrial rounded-lg p-4 text-center">
          <Sparkles className="w-5 h-5 text-primary-orange mx-auto mb-2" />
          <div className="text-sm text-ink">All clear</div>
          <div className="text-xs text-muted-industrial mt-1">No dispatch alerts detected right now.</div>
        </div>
      )}

      <div className="space-y-2">
        {insights.map((i, idx) => {
          const Icon = iconFor(i.kind);
          const tone = toneFor(i.severity);
          return (
            <div
              key={idx}
              data-testid={`ai-insight-${idx}`}
              className={`border-l-2 rounded-r-md pl-3 pr-2 py-2 text-sm flex items-start gap-2 ${tone}`}
            >
              <Icon className="w-4 h-4 mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="text-ink text-[13px] leading-snug">{i.message}</div>
                {i.detail && <div className="text-[11px] text-muted-industrial mt-0.5">{i.detail}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
